import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { bootstrapSession, type SignupStatus } from "./api";

interface SessionState {
  loading: boolean;
  authenticated: boolean;
  // Opaque WorkOS subject. Used only as a stable analytics id (never
  // email/PII) once consent is granted.
  sub?: string | undefined;
  // Authenticated but not yet provisioned = private-preview wait. The
  // App component renders the Pending gate page in that case rather
  // than mounting the dashboard / admin routes.
  provisioned: boolean;
  signup_status?: SignupStatus | null;
}

const Ctx = createContext<SessionState>({
  loading: true,
  authenticated: false,
  provisioned: false,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    loading: true,
    authenticated: false,
    provisioned: false,
  });
  useEffect(() => {
    bootstrapSession()
      .then((s) =>
        setState({
          loading: false,
          authenticated: s.authenticated,
          sub: s.sub,
          provisioned: s.provisioned ?? false,
          signup_status: s.signup_status ?? null,
        }),
      )
      .catch(() =>
        setState({
          loading: false,
          authenticated: false,
          provisioned: false,
        }),
      );
  }, []);
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  return useContext(Ctx);
}
